import React, { lazy, Suspense, useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Checkbox from '@atlaskit/checkbox';
import { FlagGroup } from '@atlaskit/flag';
import { Inline, Stack } from '@atlaskit/primitives/compiled';

import { FEEDBACK_CONTEXT_CF } from '../../../constants';

import { FeedbackButtons } from './feedback-buttons';
import { messages } from './messages';
import { AIAnswerReportDescriptionWrapper } from './styled';

const AkFeedbackCollector = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/feedback-collector" */ '@atlaskit/feedback-collector'
	).then((module) => {
		return {
			default: module.default,
		};
	}),
);

const AkFeedbackFlag = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/feedback-flag" */ '@atlaskit/feedback-collector'
	).then(({ FeedbackFlag }) => {
		return {
			default: FeedbackFlag,
		};
	}),
);

type AIFeedbackProps = {
	entrypointId: string;
	userDetails: {
		name?: string;
		id?: string;
	};
	attributes: object;
	attributesWithConsent?: object;
	onLikeButtonClickHandler?: (e?: any) => void;
	onDislikeButtonClickHandler?: (e?: any) => void;
	liked?: boolean;
	customContent?: React.ReactNode;
	featureTitle?: string;
};

export const AIFeedbackV2 = ({
	entrypointId,
	onLikeButtonClickHandler,
	onDislikeButtonClickHandler,
	userDetails,
	attributes,
	attributesWithConsent = {},
	liked: externalLike, // set like state from the outside
	customContent,
	featureTitle,
}: AIFeedbackProps): React.JSX.Element => {
	const { formatMessage, locale } = useIntl();
	const [showFeedbackModal, setFeedbackModal] = useState(false);
	const [displayFlag, setDisplayFlag] = useState(false);
	const [hasConsent, setConsent] = useState(false);
	const [liked, setLiked] = useState<boolean | undefined>(externalLike);

	const feedbackObject = {
		...attributes,
		...(hasConsent ? attributesWithConsent : {}),
	};

	const feedbackContext = Object.entries(feedbackObject)
		.map(([key, value]) => `${key}: ${value}`)
		.join(', ');

	const toggleConsentCheckbox = useCallback(() => {
		setConsent(!hasConsent);
	}, [hasConsent]);

	return (
		<>
			<Inline alignBlock="center" space="space.050">
				<FeedbackButtons
					setDisplayFlag={setDisplayFlag}
					setFeedbackModal={setFeedbackModal}
					onLikeButtonClickHandler={onLikeButtonClickHandler}
					onDislikeButtonClickHandler={onDislikeButtonClickHandler}
					liked={liked}
					setLiked={setLiked}
				/>
			</Inline>

			{showFeedbackModal ? (
				<Suspense fallback={<></>}>
					<AkFeedbackCollector
						locale={locale}
						onClose={() => {
							setFeedbackModal(false);
						}}
						onSubmit={() => {
							setDisplayFlag(true);
						}}
						customContent={
							<Stack space="space.050">
								{Object.keys(attributesWithConsent).length > 0 ? (
									<Checkbox
										label={formatMessage(
											messages.ai_answer_component_report_consent_checkbox_label,
										)}
										onChange={toggleConsentCheckbox}
										isChecked={hasConsent}
									/>
								) : undefined}
								{customContent}
							</Stack>
						}
						atlassianAccountId={userDetails.id}
						name={userDetails.name}
						entrypointId={entrypointId}
						additionalFields={[
							{
								id: FEEDBACK_CONTEXT_CF,
								value: feedbackContext,
							},
						]}
						feedbackTitle={formatMessage(messages.ai_answer_component_report_modal_title, {
							featureTitle: featureTitle ?? 'Atlassian Intelligence',
						})}
						feedbackTitleDetails={
							<AIAnswerReportDescriptionWrapper>
								{formatMessage(messages.ai_answer_component_report_summary_label)}
							</AIAnswerReportDescriptionWrapper>
						}
						feedbackGroupLabels={{
							empty: {
								fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
								selectOptionLabel: formatMessage(
									messages.ai_answer_component_report_empty_option_share_label,
								),
							},
							question: {
								fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
								selectOptionLabel: formatMessage(
									messages.ai_answer_component_report_relevance_option_label,
								),
							},
							comment: {
								fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
								selectOptionLabel: '',
							},
							bug: {
								fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
								selectOptionLabel: '',
							},
							suggestion: {
								fieldLabel: formatMessage(messages.ai_answer_component_report_details_label),
								selectOptionLabel: formatMessage(
									messages.ai_answer_component_report_other_option_label,
								),
							},
						}}
						summaryPlaceholder={formatMessage(
							messages.ai_answer_component_summary_placeholder_label,
						)}
					/>
				</Suspense>
			) : null}

			{displayFlag && (
				<FlagGroup onDismissed={() => setDisplayFlag(false)}>
					<Suspense fallback={<></>}>
						<AkFeedbackFlag
							locale={locale}
							description={formatMessage(messages.ai_answer_component_feedback_flag_description)}
						/>
					</Suspense>
				</FlagGroup>
			)}
		</>
	);
};
